import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import color from "../../../../../constants/color";
import style from "./style";

const PurchaseCreatePage = ({
  itemList,
  data,
  handleItemList,
  handleAdd,
  loading,
  supplierList,
  handleSupplierList,
}) => {
  const [formData, setFormData] = useState({
    supplier: "",
    purchaseOrderNumber: "",
    purchaseOrderDate: dayjs(),
    deliveryMethod: "",
    deliveryDate: dayjs(),
    itemInformation: [
      {
        itemId: "",
        name: "",
        description: "",
        rate: "",
        quantity: "",
        amount: "",
      },
    ],
    discountPercentage: 0,
    discountValue: 0,
    taxPercentage: 0,
    taxValue: 0,
    total: 0,
    grandTotal: 0,
  });

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProductChange = (index, field, value, newValue = null) => {
    const updatedProducts = [...formData.itemInformation];

    // Handle Autocomplete selection differently
    if (field === "name" && newValue) {
      // When a product is selected from dropdown
      if (typeof newValue === "object") {
        updatedProducts[index] = {
          ...updatedProducts[index],
          itemId: newValue._id || "",
          name: newValue.title || "",
          description: newValue.description || "",
          rate: newValue.price || newValue.purchasePrice || 0,
          quantity: updatedProducts[index].quantity || "",
          amount: "",
        };
      } else {
        // When typing manually
        updatedProducts[index][field] = value;
      }
    } else {
      // Normal field update
      updatedProducts[index][field] = value;
    }

    // Calculate amount if quantity or rate changes
    if (field === "quantity" || field === "rate") {
      const quantity =
        field === "quantity"
          ? Number(value)
          : Number(updatedProducts[index].quantity);
      const rate =
        field === "rate" ? Number(value) : Number(updatedProducts[index].rate);
      updatedProducts[index].amount = quantity * rate;
    }

    setFormData((prev) => ({
      ...prev,
      itemInformation: updatedProducts,
    }));

    calculateTotals(updatedProducts);
  };

  const calculateTotals = (products) => {
    const subtotal = products.reduce(
      (sum, item) => sum + (item.amount || 0),
      0
    );
    const discountValue = subtotal * (formData.discountPercentage / 100);
    
    const taxableAmount = subtotal - discountValue;
    const taxValue = taxableAmount * (formData.taxPercentage / 100);
    
    const grandTotal = subtotal - discountValue + taxValue;
    setFormData((prev) => ({
      ...prev,
      total: subtotal,
      discountValue,
      taxValue,
      grandTotal,
    }));
  };

  const handlePercentageChange = (field, value) => {
    const numValue = Number(value) || 0;
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        [field]: numValue,
      };

      // Recalculate with new percentages using the updated form data
      const subtotal = prev.itemInformation.reduce(
        (sum, item) => sum + (item.amount || 0),
        0
      );

      const discountValue =
        subtotal * (updatedFormData.discountPercentage / 100);
      
      const taxableAmount = subtotal - discountValue;
      const taxValue = taxableAmount * (updatedFormData.taxPercentage / 100);

      const grandTotal = subtotal - discountValue + taxValue;

      return {
        ...updatedFormData,
        total: subtotal,
        discountValue,
        taxValue,
        grandTotal,
      };
    });
  };

  const handleAddProduct = () => {
    setFormData((prev) => ({
      ...prev,
      itemInformation: [
        ...prev.itemInformation,
        {
          itemId: "",
          name: "",
          description: "",
          rate: "",
          quantity: "",
          amount: "",
        },
      ],
    }));
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...formData.itemInformation];
    updatedProducts.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      itemInformation: updatedProducts,
    }));

    calculateTotals(updatedProducts);
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData((prev) => ({
      ...prev,
      supplier: supplier._id,
    }));
  };

  const isFormValid = () => {
    if (!formData.supplier || !formData.purchaseOrderNumber) {
      return false;
    }

    // Check all products
    for (let product of formData.itemInformation) {
      if (!product.description || !product.rate || !product.quantity) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      supplier: formData.supplier,
      purchaseOrderNumber: formData.purchaseOrderNumber,
      purchaseOrderDate: formData.purchaseOrderDate.toISOString(),
      deliveryMethod: formData.deliveryMethod,
      deliveryDate: formData.deliveryDate.toISOString(),
      itemInformation: formData.itemInformation
        .filter((item) => item.name && item.rate && item.quantity) // Filter out empty items
        .map((item) => ({
          itemId: item.itemId,
          name: item.name,
          description: item.description,
          rate: Number(item.rate),
          quantity: Number(item.quantity),
          amount: Number(item.amount),
        })),
      total: Number(formData.total),
      discountPercentage: Number(formData.discountPercentage),
      discountValue: Number(formData.discountValue),
      taxPercentage: Number(formData.taxPercentage),
      taxValue: Number(formData.taxValue),
      grandTotal: Number(formData.grandTotal),
    };
    handleAdd(payload);
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Create Purchase</Typography>
        </Box>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container sx={style.mainContainer}>
          <Grid item xs={12} lg={11.8} sx={{ ...style.gridbox, px: 2 }}>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>
                  Supplier<span style={{ color: "red" }}>*</span>
                </Typography>
                <Autocomplete
                  id="supplier-autocomplete"
                  options={[
                    { _id: "add_new", name: "+ New Supplier" },
                    ...(supplierList || []),
                  ]}
                  getOptionLabel={(option) => option?.name}
                  value={selectedSupplier}
                  onChange={(_, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewSupplier"); // redirect page
                      return;
                    }
                    handleSupplierSelect(newValue);
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      placeholder="Supplier"
                      {...params}
                      sx={style.modalInput}
                      variant="outlined"
                    />
                  )}
                />
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>
                  Purchase Order<span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  placeholder="Purchase Order Number"
                  sx={style.modalInput}
                  value={formData.purchaseOrderNumber}
                  onChange={(e) =>
                    handleInputChange("purchaseOrderNumber", e.target.value)
                  }
                />
              </Box>
              <Box sx={style.inputBox}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <InputLabel sx={style.label}>
                    Date<span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <DatePicker
                    sx={style.dateInput}
                    value={formData.purchaseOrderDate}
                    onChange={(value) =>
                      handleInputChange("purchaseOrderDate", value)
                    }
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>
                  Delivery Method<span style={{ color: "red" }}>*</span>
                </Typography>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "2.5em",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Select
                    value={formData.deliveryMethod}
                    displayEmpty
                    onChange={(e) =>
                      handleInputChange("deliveryMethod", e.target.value)
                    }
                    sx={style.modalInput}
                    renderValue={
                      formData.deliveryMethod !== ""
                        ? undefined
                        : () => (
                            <span style={{ color: "#999" }}>
                              Delivery Method
                            </span>
                          )
                    }
                  >
                    <MenuItem value="Courier">Courier</MenuItem>
                    <MenuItem value="Pickup">Pickup</MenuItem>
                    <MenuItem value="Shipping">Shipping</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={style.inputBox}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <InputLabel sx={style.label}>
                    Delivery Date<span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <DatePicker
                    sx={style.dateInput}
                    value={formData.deliveryDate}
                    onChange={(value) =>
                      handleInputChange("deliveryDate", value)
                    }
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Grid>
          <Box sx={style.itemSection}>
            <Box
              sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
            ></Box>
            <Typography sx={style.gridText}>
              Item & Services<span style={{ color: "red" }}>*</span>
            </Typography>
          </Box>

          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            {formData.itemInformation.map((item, index) => {
              return (
                <Box sx={style.leaveSection} key={index}>
                  <Grid container sx={style.itemGrid}>
                    <Grid item xs={12} lg={2}>
                      <Autocomplete
                        id={`product-autocomplete-${index}`}
                        // freeSolo
                        options={[
                          { _id: "add_new", title: "+ New Item" },
                          ...(itemList || []),
                        ]}
                        getOptionLabel={(option) => option?.title || ""}
                        value={
                          itemList?.find(
                            (prod) => prod._id === item.itemId
                          ) || {
                            title: item.name,
                          }
                        }
                        onChange={(_, newValue) => {
                          if (newValue?._id === "add_new") {
                            navigate("/dashboard/addNewItem"); // redirect page
                            return;
                          }
                          handleProductChange(
                            index,
                            "name",
                            newValue?.title || "",
                            newValue
                          );
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={
                              option._id === "add_new"
                                ? { color: color.secondary }
                                : {}
                            }
                          >
                            {option.title}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            placeholder="Item"
                            {...params}
                            sx={style.modalInput}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        required
                        fullWidth
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        sx={style.modalInput}
                      />
                    </Grid>
                    <Grid item xs={12} lg={1.5}>
                      <TextField
                        required
                        fullWidth
                        type="number"
                        placeholder="Price"
                        sx={style.modalInput}
                        value={item.rate}
                        onChange={(e) =>
                          handleProductChange(index, "rate", e.target.value)
                        }
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} lg={1.5}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        type="number"
                        placeholder="QTY"
                        sx={style.modalInput}
                        value={item.quantity}
                        onChange={(e) =>
                          handleProductChange(index, "quantity", e.target.value)
                        }
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={9} lg={1.5}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Amount"
                        value={item.amount}
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={style.modalInput}
                      />
                    </Grid>
                    {index === 0 ? (
                      <Grid item xs={1.5} lg={0.5}>
                        <Box sx={style.addIcon} onClick={handleAddProduct}>
                          +
                        </Box>
                      </Grid>
                    ) : (
                      <Grid item xs={1.5} lg={0.5}>
                        <Box
                          sx={style.deleteIcon}
                          onClick={() => handleRemoveProduct(index)}
                        >
                          <DeleteOutlineIcon />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}
          </Grid>

          <Grid container spacing={0}>
            <Grid item xs={12} md={7}>
              <Box sx={style.container4}>
                <Box
                  sx={{
                    height: "140px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#202020",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      "&::before": {
                        content: '""',
                        width: "3px",
                        height: "16px",
                        backgroundColor: "#28AA4A",
                        marginRight: "8px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Discount and Tax (%)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Discount
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Discount"
                          value={formData.discountPercentage}
                          onChange={(e) =>
                            handlePercentageChange(
                              "discountPercentage",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Tax
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Tax"
                          value={formData.taxPercentage}
                          onChange={(e) =>
                            handlePercentageChange(
                              "taxPercentage",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={style.container3}>
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "140px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>Total</Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.total.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Tax ({formData.taxPercentage}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.taxValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Discount ({formData.discountPercentage}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.discountValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 1,
                      borderTop: "1px solid #eee",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                      {formData.grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid container sx={style.lastGrid}>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Grid item xs={12} lg={4.5}>
                <Box sx={{ ...style.mainBoxes, px: 2 }}>
                  <Box sx={style.createJob}>
                    <Button
                      variant="contained"
                      onClick={() => navigate("/dashboard/purchase")}
                      color="success"
                      sx={style.cancelButtons}
                    >
                      Cancel
                    </Button>
                  </Box>
                  <Box sx={style.createJob}>
                    <Button
                      disabled={!isFormValid()}
                      variant="contained"
                      type="submit"
                      color="success"
                      sx={style.jobButtons}
                    >
                      Add Purchase
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default PurchaseCreatePage;
